import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';

import { makePost } from '../../../api/ajax';
import { Field, Error } from '../../../components/Form';
import { Checkbox } from '../../../components';
import {
  initializeKeyPair,
  getPublicKeyPEM,
  encryptCredentials,
  decryptAESKey,
  decryptAESIV,
} from '../../../utils/authEncryption';
import MfaCodeForm from './MfaCodeForm';

/**
 * Handles encrypted login flow
 */
const handleEncryptedLogin = async (email, password, setErrorMessage, setSubmitting, onLoginSuccess, onMfaPending) => {
  setErrorMessage('');

  // Step 0: Generate fresh RSA key pair for this login attempt
  const keyPair = await initializeKeyPair();

  // Step 1: Get public key
  const publicKey = await getPublicKeyPEM(keyPair);

  // Step 2: Exchange keys with server
  const keyExchangePromise = new Promise((resolve) => {
    makePost('session/handshake', { publicKey }).subscribe(
      ({ response }) => resolve(response),
    );
  });

  const keyExchange = await keyExchangePromise;

  // Step 3: Decrypt AES key and IV
  const aesKey = await decryptAESKey(keyPair, keyExchange.encryptedAesKey);
  const aesIV = await decryptAESIV(keyPair, keyExchange.encryptedAesIV);

  // Step 4: Encrypt credentials
  const encryptedPayload = await encryptCredentials(
    { Email: email, Password: password },
    aesKey,
    aesIV
  );

  // Step 5: Send encrypted login request
  makePost('session/secure', {
    sessionId: keyExchange.sessionId,
    encryptedPayload,
  }).subscribe(
    ({ response }) => {
      setSubmitting(false);

      // MFA is enabled for this user and a code was (or already had been) sent to their e-mail.
      // Hold off on completing the login until the code has been verified.
      if (response?.mfaPending) {
        onMfaPending(response);
        return;
      }

      onLoginSuccess(response);
    },
    ({ response }) => {
      setSubmitting(false);
      setErrorMessage(response?.error);
    }
  );
};

/**
 * Main submit handler - always uses encrypted login
 */
const handleSubmit = (requestBody, setErrorMessage, setSubmitting, onLoginSuccess, onMfaPending) => {
  const { email, password } = requestBody;

  // Always use encrypted login (keys are generated per-login)
  handleEncryptedLogin(email, password, setErrorMessage, setSubmitting, onLoginSuccess, onMfaPending);
};

const LoginForm = ({ onLoginSuccess }) => {
  const [errorMsg, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [mfaPending, setMfaPending] = useState(false);

  const history = useHistory();

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  if (mfaPending) {
    return (
      <MfaCodeForm
        email={email}
        onLoginSuccess={onLoginSuccess}
        onBack={() => setMfaPending(false)}
      />
    );
  }

  return (
    <>
      <Formik
        initialValues={{
          email: '',
          password: '',
          acceptToS: false,
        }}
        onSubmit={({ email, password }, { setSubmitting }) => handleSubmit(
          { email, password }, setErrorMessage, setSubmitting, onLoginSuccess, () => setMfaPending(true),
        )}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, setValues, touched, isSubmitting, setFieldValue }) => (
          <Form style={{ maxWidth: '22.4375rem', margin: '0 auto' }}>
            <Field
              name="email"
              label="E-mail"
              labelStyle={labelStyle}
              type="email"
              placeholder="Insert e-mail"
              value={values.email}
              errors={errors.email}
              touched={touched.email}
              onChange={(e) => {
                setEmail(e.target.value);
                setValues({ ...values, email: e.target.value });
              }}
              required
            />
            <Field
              name="password"
              label="Password"
              labelStyle={labelStyle}
              type="password"
              placeholder="Insert password"
              value={values.password}
              errors={errors.password}
              touched={touched.password}
              required
            />
            <div className="form-group pt-2">
              <Checkbox
                label={(
                  <span>
                    I have read and accept the&nbsp;
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" onClick={stopPropagation}>Privacy Policy</a>,
                    &nbsp;including the use of strictly necessary cookies.
                  </span>)}
                name="acceptToS"
                id="acceptTos"
                checked={values.acceptToS}
                onClickHandler={() => {
                  setFieldValue('acceptToS', !values.acceptToS);
                }}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', margin: '0.625rem auto' }}
              disabled={isSubmitting || !values.acceptToS}
            >
              Login
            </button>
          </Form>
        )}
      </Formik>
      <div>
        <button
          type="button"
          className="btn-link"
          onClick={(event) => {
            event.preventDefault();
            if (email) {
              history.push(`/resetPassword/${email}`);
            } else {
              setErrorMessage('Please insert your e-mail before pressing "forgot your password"');
            }
          }}
        >
          Forgot your password?
        </button>
        {!!errorMsg && <Error>{errorMsg}</Error>}
      </div>
    </>
  );
};

const labelStyle = {
  display: 'block',
  textAlign: 'left',
  fontSize: '0.7rem',
  padding: '0.3125rem 0',
};

LoginForm.propTypes = {
  onLoginSuccess: PropTypes.func,
};

export default LoginForm;
